import { NgClass } from '@angular/common';
import {
  AfterViewInit, Component, ElementRef, EventEmitter,
  forwardRef, inject, Input, OnDestroy, OnInit, Output, ViewChild
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, UntypedFormControl, ValidationErrors, Validator } from '@angular/forms';
import { PREFERRED_COUNTRIES } from '@constants/app.constants';
import { GeoService } from '@services/feature/geo.service';
import { distinctUntilChanged } from 'rxjs';

declare var intlTelInput: any;

@Component({
  selector: 'app-calio-tel-input',
  templateUrl: './calio-tel-input.component.html',
  styleUrls: ['./calio-tel-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CalioTelInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CalioTelInputComponent),
      multi: true
    }
  ],
  imports: [FormsModule, NgClass, ReactiveFormsModule]
})

export class CalioTelInputComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator, AfterViewInit {

  private geoService = inject(GeoService);

  constructor() {
  }

  @Input() defaultCountry: string;
  @Input() public cssClass: {};
  @Input() public E164PhoneNumber: string;
  @Input() public label: string;
  @Input() public labelCssClass: string;
  @Input() public name = 'intl-tel-input-name';
  @Input() public onlyLocalized = true;
  @Input() public options: any = {};
  @Input() public required = false;
  @Input() public isDisabled = false;

  @Output() public blurEvent: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('intlTelInput') private _inputElement: ElementRef;

  private _intlTelInput: any;
  private shadowRootClickGuard: { root: ShadowRoot, listener: (event: Event) => void } | undefined;
  private onTouch: Function;
  private onModelChange: Function;
  private onValidatorChange: Function;
  readonly preferredCountries = PREFERRED_COUNTRIES;

  telInputControl: UntypedFormControl = new UntypedFormControl();

  private static modifyCountryData(): void {
    (window as any).intlTelInput.getCountryData().forEach((country: any) =>
      country.name = country.name.replace(/.+\((.+)\)/, '$1'));
  }

  ngOnInit(): void {
    this.telInputControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((inputValue: string) => {
        if (!inputValue) {
          if (this.onModelChange) {
            this.onModelChange(inputValue);
          }
        }
        if (inputValue && this.onModelChange) {
          this.setPhoneNumber(inputValue);
          if (this.onTouch && !this.telInputControl.touched) {
            this.onTouch();
          }
          if (this.onValidatorChange) {
            this.onValidatorChange();
          }
        }
      }
      );

    if (this.isDisabled) {
      this.telInputControl.disable();
    }
  }

  onClick(): void {
    if (this.onTouch) {
      this.onTouch();
    }
  }

  onBlur(): void {
    if (this.onTouch) {
      this.onTouch();
    }
    this.blurEvent.emit();
  }

  public ngAfterViewInit(): void {
    this.onlyLocalized && CalioTelInputComponent.modifyCountryData();

    const fullscreenPopup = CalioTelInputComponent.useFullscreenPopup();
    this.options = {
      initialCountry: 'auto',
      separateDialCode: true,
      preferredCountries: this.preferredCountries,
      formatOnDisplay: true,
      autoHideDialCode: true,
      phoneValidation: true,
      loadUtils: () => import('intl-tel-input/build/js/utils.js'),
      // null = accept any valid phone number type (mobile, landline, VOIP, toll-free, etc.)
      // Restricting to specific types like ["MOBILE", "FIXED_LINE"] would reject numbers
      // classified as FIXED_LINE_OR_MOBILE (ambiguous) — common in US, Canada, and others.
      validationNumberTypes: null,
      countrySearch: true,
      geoIpLookup: callback => {
        this.geoService.lookup()
          .then(data => callback(data?.country || 'ch'))
          .catch(() => callback('ch'));
      },
      // Shadow DOM web component: keep the mobile fullscreen popup INSIDE the shadow tree. The
      // library appends it to `document.body` by default, where the host page's global CSS restyles
      // it (the country list is a plain `ul` / `li` / `a` structure) and the widget's own
      // intl-tel-input styles do not reach it. Only for the fullscreen popup: a container is
      // consumed when the markup is built and would turn the DESKTOP dropdown into a JS-positioned
      // floating container too, so the library's own mobile heuristic is mirrored here and the
      // inline desktop dropdown stays exactly as it was.
      useFullscreenPopup: fullscreenPopup,
      dropdownContainer: fullscreenPopup ? this.shadowRootDropdownContainer() : null,
    };

    const intlTelInputInstance = intlTelInput;
    this._intlTelInput = intlTelInputInstance(this._inputElement.nativeElement, this.options);

    this.installShadowRootClickGuard();
  }

  public ngOnDestroy(): void {
    this.shadowRootClickGuard?.root.removeEventListener('click', this.shadowRootClickGuard.listener);
    this.shadowRootClickGuard = undefined;
  }

  /** intl-tel-input's own default for `useFullscreenPopup` (v25: mobile UA, or a narrow viewport, or coarse pointer + short viewport). */
  private static useFullscreenPopup(): boolean {
    if (typeof navigator === 'undefined' || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    const isMobileUserAgent = /Android.+Mobile|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const mq = (query: string) => window.matchMedia(query).matches;
    return isMobileUserAgent || mq('(max-width: 500px)') || (mq('(pointer: coarse)') && mq('(max-height: 600px)'));
  }

  /** The widget's own root when this input renders inside a shadow root, otherwise `null` (= library default, `document.body`). */
  private shadowRootDropdownContainer(): HTMLElement | null {
    const root = this._inputElement.nativeElement.getRootNode();
    if (typeof ShadowRoot === 'undefined' || !(root instanceof ShadowRoot)) {
      return null;
    }
    return root.querySelector<HTMLElement>('.cbsw-smart-widget');
  }

  /**
   * Shadow DOM web component only. intl-tel-input closes its open country dropdown from a `click`
   * listener on `document.documentElement` that inspects `event.target.closest(<dropdown>)`. Seen
   * from the document, every click that originates inside a shadow tree is re-targeted to the
   * shadow HOST, so a click on the dropdown's own search field (or scrollbar) looks like a click
   * outside and closes the list. Clicks that start inside the dropdown are therefore stopped at the
   * shadow root: they never reach the document-level handler, and everything below (the country
   * list's own listeners, Angular's) has already run. Side effect, accepted: those clicks are also
   * invisible to the host page's own document-level listeners (analytics). The mobile fullscreen
   * popup lives inside the widget root as well (`dropdownContainer`), so the same guard covers it;
   * a click on its backdrop still propagates and closes it.
   */
  private installShadowRootClickGuard(): void {
    const root = this._inputElement.nativeElement.getRootNode();
    if (typeof ShadowRoot === 'undefined' || !(root instanceof ShadowRoot)) {
      return;
    }
    const listener = (event: Event) => {
      const target = event.target as Element | null;
      if (target?.closest?.('.iti__dropdown-content')) {
        event.stopPropagation();
      }
    };
    root.addEventListener('click', listener);
    this.shadowRootClickGuard = { root, listener };
  }

  setPhoneNumber(value: string): void {
    if (this._intlTelInput) {
      if (!!value) {
        this._intlTelInput.setNumber(value);
      }
      this.i18nizePhoneNumber();
    } else {
    }
  }

  public i18nizePhoneNumber(): void {
    if (!this._intlTelInput) {
      return;
    }

    if (Number(this._intlTelInput.telInput.style.paddingLeft.replace('px', '')) > 100) {
      const widthOfCountryDialCode = this._intlTelInput.selectedDialCode.parentElement.clientWidth;
      this._intlTelInput.telInput.style.paddingLeft = `${(widthOfCountryDialCode + 3.97)}px`;
    }
    this.E164PhoneNumber = this._intlTelInput.getNumber();
    if (this.E164PhoneNumber) {
      this.onModelChange(this.E164PhoneNumber);
    } else {
      if (this._intlTelInput.selectedDialCode && this._intlTelInput.selectedDialCode.innerText) {
        if (this.telInputControl.value && this.telInputControl.value[0] === '+') {
          this.E164PhoneNumber = this._intlTelInput.selectedDialCode.innerText + this._intlTelInput.telInput.value;
        } else {
          this.E164PhoneNumber = this._intlTelInput.selectedDialCode.innerText + (this.telInputControl.value ? this.telInputControl.value : '');
        }
        this.onModelChange(this.E164PhoneNumber);
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(obj: any): void {
    this.telInputControl.setValue(obj);
  }

  validate(control: AbstractControl): ValidationErrors {
    if (control.value) {
      const selectedCountryCode = this._intlTelInput.getSelectedCountryData();
      if (control.value === `+${selectedCountryCode.dialCode}`) {
        if (this.required) {
          control.setErrors({
            required: true
          });
          return control.errors;
        }
      } else if (!this._intlTelInput.isValidNumber() && this._intlTelInput.isValidNumber() !== null) {
        control.setErrors({
          invalidNumber: true
        });
        return control.errors;
      }
    }
    return null;
  }

  registerOnValidatorChange?(fn: any): void {
    this.onValidatorChange = fn;
  }
}
